/**
 * Ficha Municipal v2 — Wow 1 refinado del Sprint 13.
 *
 * Orchestrator de las 4 capas componibles:
 * - Capa 0 (`FichaHeader`): identidad + párrafo ejecutivo auto-generado.
 * - Capa 1 (`ScoreHero`): Score Alpha + drill-down + Show your work.
 * - Capa 2 (`ContextLayer` con 3 cards): top sectores + brecha COP +
 *   indicadores duros.
 * - Capa 2.5 (`DepartmentHeatmap`): contexto regional (Sprint 14.5).
 * - Capa 3 (`NextActionsBriefing`): la siguiente acción concreta.
 *   Reemplazó al legacy `ActionPlanSection` (3 cards con CTA repetido);
 *   su componente `CalloutCard` se borró en el Sprint 23 al confirmar que
 *   ya no lo importaba nadie.
 *
 * Lee 4 queries en paralelo (profile, score, action plan, heatmap).
 * El modal "Show your work" carga lazy desde `/score-details` cuando
 * el usuario lo abre.
 *
 * Sprint 18.4.2 (P1-6 audit, mayo 20, 2026): este archivo era de
 * 1234 LOC mezclando 8 subcomponentes con el orchestrator. Ahora
 * cada subcomponente vive en `pages/ficha-municipal/` y este archivo
 * queda enfocado en componer la página + small helpers (Breadcrumb,
 * ScrollSection, skeletons, ErrorCard).
 *
 * Ver:
 * - PRD: docs/product/ficha-municipal-prd.md
 * - System design: docs/engineering/architecture/ficha-municipal.md
 * - ADR-017: docs/engineering/adrs/ADR-017-score-alpha-design.md
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { DepartmentHeatmap } from "@/components/DepartmentHeatmap";
import { ShowYourWorkModal } from "@/components/ShowYourWorkModal";
import { track } from "@/lib/analytics";
import {
  ApiError,
  fetchDepartmentHeatmap,
  fetchMunicipalityProfile,
  fetchMunicipalityScore,
  fetchNextActionsBriefing,
  type MunicipalityScoreDTO,
} from "@/lib/api";
import { useMunicipality } from "@/lib/municipality";
import type { MunicipalityProfileDTO } from "@/lib/types";

import { FichaHeader } from "./ficha-municipal/FichaHeader";
import { GapVsPeerBest } from "./ficha-municipal/GapVsPeerBest";
import { HardIndicators } from "./ficha-municipal/HardIndicators";
import { NextActionsBriefing } from "./ficha-municipal/NextActionsBriefing";
import { ProjectsSgrSection } from "./ficha-municipal/ProjectsSgrSection";
import { ScoreHero } from "./ficha-municipal/ScoreHero";
import { TopHistoricalSectors } from "./ficha-municipal/TopHistoricalSectors";

export function FichaMunicipal() {
  const { dane } = useParams<{ dane: string }>();
  // Issue #561: un DANE que no son 5 dígitos no existe. Se responde igual
  // que un 404 y sin llamar al API: antes eran 4 endpoints en 422, cada uno
  // reintentado, y una tarjeta que hablaba de un formulario.
  const daneValido = dane !== undefined && /^\d{5}$/.test(dane);
  const { daneCode: tenantDane } = useMunicipality();
  const [showWorkOpen, setShowWorkOpen] = useState(false);
  // Guard para que `municipality_score_viewed` se dispare 1 vez por
  // (dane, has_score) — evitamos doble fire en remounts.
  const viewedRef = useRef<string | null>(null);

  // 2 queries en paralelo. Profile da identidad DIVIPOLA; score da
  // el wow. Si profile carga primero, ya renderizamos el header.
  const profile = useQuery({
    queryKey: ["muni-profile", dane],
    queryFn: () => fetchMunicipalityProfile(dane as string),
    enabled: daneValido,
    retry: (count, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 400 || err.status === 422)
      ) {
        return false;
      }
      return count < 2;
    },
  });

  const score = useQuery({
    queryKey: ["muni-score", dane],
    queryFn: () => fetchMunicipalityScore(dane as string),
    enabled: daneValido,
    retry: (count, err) => {
      // 503 = score aún no precalculado, no reintentar
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 400 || err.status === 422 || err.status === 503)
      ) {
        return false;
      }
      return count < 2;
    },
  });

  // Sprint Pre-Demo Alcaldesa: reemplaza /action-plan por
  // /next-actions-briefing. El payload trae 3 bloques + 1 CTA en vez
  // de 3 callouts con CTA repetido.
  //
  // Sprint Blindaje — SEC-1 (julio 2026): el endpoint ya no acepta
  // `tenant_id` del cliente — el backend lo deriva del JWT vía
  // `ensure_context`. Antes esta página hacía un fetch adicional a
  // `/onboarding/context` solo para resolver el tenant y pasarlo acá;
  // ese query completo se eliminó junto con el param.
  // Un 401/403 tampoco se reintenta (p.ej. ficha de un muni ajeno al
  // tenant del usuario): data queda null y la seccion se oculta. Venia
  // del hotfix #369 en `main`; se conserva al mergear v1.1.0.
  const nextActions = useQuery({
    queryKey: ["muni-next-actions", dane],
    queryFn: () => fetchNextActionsBriefing(dane as string),
    enabled: daneValido,
    retry: (count, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 400 ||
          err.status === 401 ||
          err.status === 403 ||
          err.status === 404 ||
          err.status === 422)
      ) {
        return false;
      }
      return count < 2;
    },
  });

  // Heatmap regional (Sprint 14.5, movido del Radar a la Ficha).
  // Departamento inferido de los 2 primeros dígitos del DANE. Carga
  // en paralelo con score/profile; el componente muestra skeleton
  // mientras tanto. staleTime alineado con el cache HTTP del backend.
  // Issue #726: el muni que se resalta como "Tu municipio" es el del
  // tenant, no el de la URL — el ranking enlaza a las fichas de los
  // vecinos, así que la URL puede ser la de otro municipio.
  const departmentCode = (dane ?? "").slice(0, 2);
  const heatmap = useQuery({
    queryKey: ["dept-heatmap", departmentCode, tenantDane],
    queryFn: () => fetchDepartmentHeatmap(departmentCode, tenantDane),
    enabled: daneValido,
    staleTime: 60 * 60 * 1000, // 1h
    retry: (count, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 400 || err.status === 422)
      ) {
        return false;
      }
      return count < 2;
    },
  });

  // Telemetría heatmap_viewed — 1 vez por (dane, depto) cuando carga.
  const heatmapViewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!heatmap.data || !dane) return;
    const key = `${dane}:${departmentCode}`;
    if (heatmapViewedRef.current === key) return;
    heatmapViewedRef.current = key;
    track({
      event_name: "heatmap_viewed",
      properties: {
        dane_code: dane,
        department_code: departmentCode,
        total_munis: heatmap.data.total_munis,
      },
    });
  }, [heatmap.data, dane, departmentCode]);

  // Telemetría — `municipality_score_viewed` cuando el score termina
  // de cargar. Lo disparamos una sola vez por par (dane, has_score)
  // para no inflar el conteo con re-renders.
  useEffect(() => {
    if (!dane || !score.data) return;
    const fingerprint = `${dane}:${score.data.has_score}`;
    if (viewedRef.current === fingerprint) return;
    viewedRef.current = fingerprint;
    track({
      event_name: "municipality_score_viewed",
      properties: {
        dane_code: dane,
        has_score: score.data.has_score,
        score_alpha: score.data.score_alpha,
        percentile: score.data.percentile,
        peer_group_size: score.data.peer_group_size,
        degraded_reason: score.data.degraded_reason,
      },
    });
  }, [dane, score.data]);

  if (!dane) {
    return <ErrorCard message="Código DANE faltante en la URL." />;
  }

  if (!daneValido) {
    return <ErrorCard message={`Municipio con DANE ${dane} no encontrado.`} />;
  }

  if (profile.isLoading) {
    return <FichaSkeleton />;
  }

  if (profile.isError) {
    const err = profile.error;
    if (err instanceof ApiError && err.status === 404) {
      return <ErrorCard message={`Municipio con DANE ${dane} no encontrado.`} />;
    }
    return <ErrorCard message={err instanceof Error ? err.message : "Error cargando el perfil."} />;
  }

  if (!profile.data) return null;

  // Issues #726 y #746: el ranking regional enlaza a las fichas de los
  // vecinos, así que la URL puede ser la de otro municipio. En ese caso cada
  // tarjeta lo nombra en vez de hablar de "tu" municipio.
  const municipioAjeno = tenantDane === dane ? null : profile.data.name;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Breadcrumb />
      <FichaHeader
        profile={profile.data}
        score={score.data ?? null}
        isLoadingScore={score.isLoading}
      />

      {/* Capa 1 — Score Alpha */}
      <ScoreHero
        score={score.data ?? null}
        isLoading={score.isLoading}
        error={score.error}
        municipioAjeno={municipioAjeno}
        onShowWork={() => {
          track({
            event_name: "municipality_show_work_opened",
            properties: { dane_code: dane },
          });
          setShowWorkOpen(true);
        }}
      />

      {/* Capa 1.5 — Drill-down proyectos SGR (Sprint Pre-Demo Alcaldesa Fase 2)
          Solo si el muni tiene score real (no degraded por cat_1_3 o no_sgr_data). */}
      {score.data?.has_score && score.data.degraded_reason === null && (
        <ProjectsSgrSection
          dane={dane}
          captacionTotalCop={score.data.captacion_total_cop}
          municipioAjeno={municipioAjeno}
        />
      )}

      {/* Capa 2 — Contexto */}
      <ScrollSection sectionId="context" dane={dane}>
        <ContextLayer
          score={score.data ?? null}
          isLoading={score.isLoading}
          profile={profile.data}
          municipioAjeno={municipioAjeno}
        />
      </ScrollSection>

      {/* Capa 2.5 — Heatmap regional (Sprint 14.5, movido del Radar).
          Coloca el muni en su contexto departamental. Skeleton corto
          mientras carga; oculta entero si el endpoint falla (no es
          info crítica como el score). */}
      <ScrollSection sectionId="regional_map" dane={dane}>
        <section
          className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-xs"
          data-testid="ficha-regional-heatmap"
        >
          {heatmap.isLoading && <div className="h-[300px] animate-pulse rounded-xl bg-slate-100" />}
          {heatmap.isError && (
            // Falla silenciosa con mensaje suave — el resto de la ficha
            // sigue siendo útil sin esto.
            <p className="text-sm text-slate-500">
              No se pudo cargar el mapa regional. El resto de la ficha sigue disponible.
            </p>
          )}
          {heatmap.data && (
            <DepartmentHeatmap dane={dane} focalDane={tenantDane} initialData={heatmap.data} />
          )}
        </section>
      </ScrollSection>

      {/* Capa 3 — Próximas Acciones (briefing strategic + FOMO).
          Sprint Pre-Demo Alcaldesa: reemplaza el legacy ActionPlanSection
          (3 cards repetidas con CTAs idénticos) por 1 narrativa con 3
          bloques y 1 CTA único. El componente se auto-oculta si
          `has_content=false`. */}
      <ScrollSection sectionId="action_plan" dane={dane}>
        {nextActions.isLoading ? (
          <div className="h-32 animate-pulse rounded-2xl bg-slate-100" />
        ) : (
          <NextActionsBriefing data={nextActions.data ?? null} />
        )}
      </ScrollSection>

      {/* Modal Show your work — lazy mount */}
      {showWorkOpen && (
        <ShowYourWorkModal
          dane={dane}
          municipioAjeno={municipioAjeno}
          onClose={() => setShowWorkOpen(false)}
        />
      )}
    </div>
  );
}

// =====================================================================
// Breadcrumb — navegación de retorno al Radar
// =====================================================================

function Breadcrumb() {
  return (
    <nav className="text-xs text-slate-500">
      <Link
        to="/radar"
        className="inline-flex items-center gap-1 hover:text-brand-700"
        data-testid="ficha-breadcrumb-radar"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
        Volver al Radar
      </Link>
    </nav>
  );
}

// =====================================================================
// Orchestrators internos — composers thin sobre las cards de subcomponentes
// =====================================================================

/**
 * Compose las 3 cards de Capa 2 (top sectores + gap + indicadores duros).
 * Es un thin orchestrator — la lógica de cada card vive en su archivo.
 */
function ContextLayer({
  score,
  isLoading,
  profile,
  municipioAjeno,
}: {
  score: MunicipalityScoreDTO | null;
  isLoading: boolean;
  profile: MunicipalityProfileDTO;
  municipioAjeno: string | null;
}) {
  if (isLoading) {
    return <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />;
  }
  // Sin score (falló /score o aún no está precalculado): top sectores y
  // brecha dependen de él, pero población y MDM vienen del profile. Se
  // pinta la card de Indicadores con lo que sí hay en vez de esconder
  // toda la capa (#559). El hero de arriba ya dice por qué falta el score.
  if (!score) {
    return (
      <section data-testid="ficha-context-layer" className="grid gap-4 md:grid-cols-2">
        <HardIndicators score={null} profile={profile} />
      </section>
    );
  }

  return (
    <section data-testid="ficha-context-layer" className="grid gap-4 md:grid-cols-2">
      <TopHistoricalSectors items={score.historical_top_sectors} />
      <GapVsPeerBest score={score} municipioAjeno={municipioAjeno} />
      <HardIndicators score={score} profile={profile} />
    </section>
  );
}

// =====================================================================
// ScrollSection — IntersectionObserver para `municipality_section_scrolled`
// =====================================================================

/**
 * Envuelve una sección de la ficha y dispara `municipality_section_scrolled`
 * la primera vez que >=25% del contenedor entra en viewport.
 *
 * Threshold 0.25 (no 0.5): si una sección es más alta que ~2x el viewport
 * (común en mobiles pequeños o pantallas con zoom), el ratio máximo nunca
 * llega a 0.5 y el evento no dispararía nunca. 0.25 da margen suficiente
 * para que cualquier viewport razonable dispare el evento al scrollear.
 *
 * No usa librería — IntersectionObserver es nativo del browser. Si el
 * browser no lo soporta (raro hoy), el guard de `'IntersectionObserver' in window`
 * evita romper la página.
 */
function ScrollSection({
  sectionId,
  dane,
  children,
}: {
  sectionId: string;
  dane: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const fired = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !fired.current) {
            fired.current = true;
            track({
              event_name: "municipality_section_scrolled",
              properties: { dane_code: dane, section: sectionId },
            });
            observer.disconnect();
          }
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [dane, sectionId]);

  return <div ref={ref}>{children}</div>;
}

// =====================================================================
// Skeleton + Error de página completa
// =====================================================================

function FichaSkeleton() {
  return (
    <div data-testid="ficha-skeleton" className="mx-auto max-w-5xl animate-pulse space-y-6">
      <div className="h-4 w-24 rounded bg-slate-200" />
      <div className="h-32 rounded-2xl bg-slate-200" />
      <div className="h-40 rounded-2xl bg-slate-200" />
      <div className="grid gap-4 md:grid-cols-2">
        <div className="h-48 rounded-2xl bg-slate-200" />
        <div className="h-48 rounded-2xl bg-slate-200" />
      </div>
      <div className="h-32 rounded-2xl bg-slate-200" />
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-red-50 p-6">
      <h1 className="text-lg font-bold text-red-900">Error</h1>
      <p className="mt-2 text-sm text-red-800">{message}</p>
      <Link
        to="/radar"
        className="mt-4 inline-block text-xs font-semibold text-red-700 hover:underline"
      >
        ← Volver al Radar
      </Link>
    </div>
  );
}
